<template>
  <transition
    name="clipboard-drawer"
    @after-leave="onDrawerTransitionLeave"
  >
    <div
      v-if="value"
      ref="drawerRoot"
      class="clipboard-drawer"
      :class="drawerOverlayClass"
      :style="drawerOverlayStyle"
      role="dialog"
      aria-modal="true"
      aria-labelledby="clipboard-drawer-title"
    >
      <div
        v-if="showInternalBackdrop"
        class="clipboard-drawer__backdrop"
        @click="close"
      />
      <div
        class="clipboard-drawer__panel"
        tabindex="-1"
      >
        <button
          type="button"
          class="clipboard-drawer__close"
          aria-label="Close"
          @click="close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
          <span class="sr-only">Close</span>
        </button>

        <div class="clipboard-drawer__head">
          <div class="clipboard-drawer__head-top">
            <h2
              id="clipboard-drawer-title"
              class="clipboard-drawer__title"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="clipboard-drawer__title-icon"
                aria-hidden="true"
              >
                <rect
                  width="8"
                  height="4"
                  x="8"
                  y="2"
                  rx="1"
                  ry="1"
                />
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
              </svg>
              Clipboard
            </h2>
            <div class="clipboard-drawer__head-actions">
              <button
                v-if="showSaveCurrent"
                type="button"
                class="clipboard-drawer__save-current"
                :disabled="!canSaveCurrent || saving"
                @click="saveCurrentDraft"
              >
                Save current
              </button>
              <button
                type="button"
                class="clipboard-drawer__new-btn"
                title="New item"
                :aria-expanded="showComposer ? 'true' : 'false'"
                @click="openNewItem()"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14" />
                  <path d="M12 5v14" />
                </svg>
              </button>
            </div>
          </div>
          <p class="clipboard-drawer__sub">
            {{ headerSubtitle }}
          </p>
          <div class="clipboard-drawer__search-wrap">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="clipboard-drawer__search-icon"
              aria-hidden="true"
            >
              <circle
                cx="11"
                cy="11"
                r="8"
              />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              v-model.trim="searchQuery"
              type="search"
              class="clipboard-drawer__search-input"
              placeholder="Search clipboard…"
              autocomplete="off"
            >
            <button
              type="button"
              class="clipboard-drawer__sort-btn"
              :title="`Sort: ${sortOrder}`"
              @click="toggleSortOrder"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="m21 16-4 4-4-4" />
                <path d="M17 20V4" />
                <path d="m3 8 4-4 4 4" />
                <path d="M7 4v16" />
              </svg>
              {{ sortLabel }}
            </button>
          </div>
          <div class="clipboard-drawer__filters">
            <button
              v-for="filter in filters"
              :key="filter.value"
              type="button"
              class="clipboard-drawer__filter"
              :class="{ 'clipboard-drawer__filter--active': activeFilter === filter.value }"
              @click="setFilter(filter.value)"
            >
              {{ filter.label }}
            </button>
          </div>
        </div>

        <ClipboardComposer
          v-if="showComposer"
          ref="composer"
          :form.sync="form"
          :bulk-paste.sync="bulkPaste"
          :saving="saving"
          @cancel="cancelComposer"
          @save="saveItem"
        />

        <div class="clipboard-drawer__scroll">
          <div class="clipboard-drawer__scroll-inner">
            <div
              v-if="loading"
              class="clipboard-drawer__empty"
            >
              <p class="clipboard-drawer__empty-title">
                Loading…
              </p>
            </div>
            <div
              v-else-if="loadError"
              class="clipboard-drawer__empty"
            >
              <p class="clipboard-drawer__empty-title clipboard-drawer__empty-text--error">
                {{ loadError }}
              </p>
              <button
                type="button"
                class="clipboard-drawer__btn clipboard-drawer__btn--ghost clipboard-drawer__retry"
                @click="loadItems"
              >
                Retry
              </button>
            </div>
            <div
              v-else-if="displayedItems.length === 0"
              class="clipboard-drawer__empty"
            >
              <svg
                v-if="!hasActiveSearch"
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="clipboard-drawer__empty-icon"
                aria-hidden="true"
              >
                <rect
                  width="8"
                  height="4"
                  x="8"
                  y="2"
                  rx="1"
                  ry="1"
                />
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
              </svg>
              <p class="clipboard-drawer__empty-title">
                {{ listEmptyState.title }}
              </p>
              <p class="clipboard-drawer__empty-hint">
                {{ listEmptyState.hint }}
              </p>
              <button
                v-if="listEmptyState.addLabel"
                type="button"
                class="clipboard-drawer__empty-add"
                @click="openNewItem(listEmptyState.addType)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14" />
                  <path d="M12 5v14" />
                </svg>
                {{ listEmptyState.addLabel }}
              </button>
            </div>
            <div
              v-else
              class="clipboard-drawer__list"
            >
              <ClipboardItemCard
                v-for="item in displayedItems"
                :key="item.id"
                :item="item"
                :insert-only="insertOnly"
                :deleting="deletingId === item.id"
                :inline-editing="inlineEditingId === item.id"
                :saving="saving && inlineEditingId === item.id"
                :expanded="!!expandedNoteIds[item.id]"
                @insert="insertItem"
                @copy="copyItem"
                @delete="confirmDelete"
                @edit="startInlineEdit"
                @cancel-edit="cancelInlineEdit"
                @save-edit="saveInlineEdit"
                @toggle-expand="toggleNoteExpand"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import ClipboardComposer from './ClipboardComposer.vue';
import ClipboardItemCard from './ClipboardItemCard.vue';
import {
  CLIPBOARD_ALL_META,
  CLIPBOARD_FILTER_ALL,
  CLIPBOARD_SORT_NEWEST,
  CLIPBOARD_SORT_OLDEST,
  CLIPBOARD_DEFAULT_TYPE,
  CLIPBOARD_TYPES,
  CLIPBOARD_TYPE_META,
  normalizeClipboardType,
} from './clipboard-constants';
import {
  matchesClipboardSearch,
  sortClipboardItems,
} from './clipboard-formatters';
import {
  createClipboardItem,
  deleteClipboardItem,
  fetchClipboardItems,
  updateClipboardItem,
} from '../../../services/api/clipboard';
import { copyTextToClipboard } from '../../../utils';

const EMPTY_FORM = () => ({
  type: CLIPBOARD_DEFAULT_TYPE,
  title: '',
  content: '',
});

export default {
  name: 'ClipboardDrawer',

  components: {
    ClipboardComposer,
    ClipboardItemCard,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    insertOnly: {
      type: Boolean,
      default: false,
    },
    /** Current chat / field draft for "Save current" (Creative Studio prompt text). */
    draftText: {
      type: String,
      default: '',
    },
    /**
     * Filter tab to pre-select each time the drawer opens (e.g. 'prompt' when opened
     * from a Variant editbox's sparkle icon). Null keeps whatever tab was last active.
     * (POR-13563-2)
     */
    initialFilter: {
      type: String,
      default: null,
    },
    /** When true, positions over a parent (e.g. layout sidebar) instead of the full viewport. */
    contained: {
      type: Boolean,
      default: false,
    },
    /** When contained, leave the vertical tab nav strip clickable (layout sidebar). */
    reserveNav: {
      type: Boolean,
      default: false,
    },
    /** Full-height panel over the layout sidebar body (Lovable-style). */
    layoutSidebar: {
      type: Boolean,
      default: false,
    },
    /** Fixed overlay aligned to the layout sidebar (escapes b-sidebar transform stacking). */
    sidebarOverlay: {
      type: Boolean,
      default: false,
    },
    /** Width when `sidebarOverlay` is true (e.g. layout sidebar `430px`). */
    overlayWidth: {
      type: String,
      default: '430px',
    },
    sidebarOnRight: {
      type: Boolean,
      default: true,
    },
    /** Pixel bounds for layout sidebar overlay (`{ top, left, width, height }`). */
    anchorRect: {
      type: Object,
      default: null,
    },
    /** Mount overlay on document.body (required for layout-sidebar custom element). */
    portalToBody: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      items: [],
      loading: false,
      loadError: null,
      saving: false,
      deletingId: null,
      activeFilter: CLIPBOARD_FILTER_ALL,
      searchQuery: '',
      sortOrder: CLIPBOARD_SORT_NEWEST,
      showComposer: false,
      bulkPaste: false,
      editingItem: null,
      inlineEditingId: null,
      expandedNoteIds: {},
      form: EMPTY_FORM(),
      typeOptions: CLIPBOARD_TYPES,
      filters: [
        { value: CLIPBOARD_FILTER_ALL, label: 'All' },
        ...CLIPBOARD_TYPES,
      ],
    };
  },

  computed: {
    shouldPortalToBody() {
      return this.portalToBody && this.sidebarOverlay;
    },

    showInternalBackdrop() {
      return !this.layoutSidebar && !(this.sidebarOverlay && this.anchorRect);
    },

    drawerOverlayClass() {
      return {
        'clipboard-drawer--contained': this.contained,
        'clipboard-drawer--contained-reserve-nav': this.contained && this.reserveNav,
        'clipboard-drawer--layout-sidebar': this.layoutSidebar,
        'clipboard-drawer--sidebar-overlay': this.sidebarOverlay,
        'clipboard-drawer--sidebar-overlay-full': this.sidebarOverlay && Boolean(this.anchorRect),
        'clipboard-drawer--sidebar-left': this.sidebarOverlay && !this.sidebarOnRight,
      };
    },

    drawerOverlayStyle() {
      if (!this.sidebarOverlay) {
        return null;
      }

      const zIndex = '10050';

      if (this.anchorRect) {
        return {
          ...this.anchorRect,
          zIndex,
          position: 'fixed',
        };
      }

      return {
        position: 'fixed',
        top: 0,
        bottom: 0,
        height: '100%',
        width: this.overlayWidth,
        zIndex,
        left: this.sidebarOnRight ? 'auto' : 0,
        right: this.sidebarOnRight ? 0 : 'auto',
      };
    },

    sortLabel() {
      return this.sortOrder === CLIPBOARD_SORT_OLDEST ? 'Oldest' : 'Newest';
    },

    hasActiveSearch() {
      return Boolean(String(this.searchQuery || '').trim());
    },

    displayedItems() {
      let list = this.items;

      if (this.activeFilter !== CLIPBOARD_FILTER_ALL) {
        list = list.filter((item) => item.type === this.activeFilter);
      }

      if (this.hasActiveSearch) {
        list = list.filter((item) => matchesClipboardSearch(item, this.searchQuery));
      }

      return sortClipboardItems(list, this.sortOrder);
    },

    canSave() {
      return Boolean(this.form.content?.trim());
    },

    headerSubtitle() {
      if (this.showComposer && CLIPBOARD_TYPE_META[this.form.type]) {
        return CLIPBOARD_TYPE_META[this.form.type].subtitle;
      }
      return CLIPBOARD_ALL_META.subtitle;
    },

    listEmptyState() {
      if (this.hasActiveSearch) {
        return {
          title: 'No results',
          hint: 'Try a different search or clear the search field.',
          addLabel: null,
          addType: 'prompt',
        };
      }

      if (this.activeFilter === CLIPBOARD_FILTER_ALL) {
        return {
          title: CLIPBOARD_ALL_META.emptyTitle,
          hint: CLIPBOARD_ALL_META.emptyHint,
          addLabel: null,
          addType: 'prompt',
        };
      }

      const meta = CLIPBOARD_TYPE_META[this.activeFilter];
      return {
        title: meta.emptyTitle,
        hint: meta.emptyHint,
        addLabel: meta.addLabel,
        addType: this.activeFilter,
      };
    },

    showSaveCurrent() {
      // Only offer "Save current" when there is draft text to save — an empty editbox
      // has nothing to save, so the action is hidden entirely. (POR-13563-2)
      return this.insertOnly
        && this.canSaveCurrent
        && (this.activeFilter === CLIPBOARD_FILTER_ALL || this.activeFilter === 'prompt');
    },

    canSaveCurrent() {
      return Boolean(String(this.draftText || '').trim());
    },
  },

  watch: {
    value(open) {
      if (open) {
        // Opened from a specific context (e.g. a Variant editbox sparkle) → jump to the
        // requested tab so the relevant items are shown up front. (POR-13563-2)
        if (this.initialFilter) {
          this.activeFilter = this.initialFilter;
        }
        this.loadItems();
        if (this.shouldPortalToBody) {
          this.$nextTick(() => this.attachDrawerToBody());
        }
      } else {
        this.dismissComposer();
        this.cancelInlineEdit();
        this.expandedNoteIds = {};
      }
    },
  },

  mounted() {
    this.$root.$on('clipboard:item-created', this.onExternalItemCreated);
  },

  beforeDestroy() {
    this.$root.$off('clipboard:item-created', this.onExternalItemCreated);
    this.detachDrawerFromBody();
  },

  methods: {
    attachDrawerToBody() {
      const el = this.$refs.drawerRoot;
      if (!el || el.parentNode === document.body) {
        return;
      }

      if (!this._portalHome) {
        this._portalHome = {
          parent: el.parentNode,
          next: el.nextSibling,
        };
      }

      this._portalEl = el;
      document.body.appendChild(el);
    },

    detachDrawerFromBody() {
      const el = this._portalEl || this.$refs.drawerRoot;
      const home = this._portalHome;

      if (!el || !home?.parent || el.parentNode !== document.body) {
        return;
      }

      home.parent.insertBefore(el, home.next);
      this._portalHome = null;
      this._portalEl = null;
    },

    onDrawerTransitionLeave() {
      if (this.shouldPortalToBody) {
        this.detachDrawerFromBody();
      }
    },
    typeMeta(type) {
      return CLIPBOARD_TYPE_META[type] || CLIPBOARD_TYPE_META.note;
    },

    close() {
      this.$emit('input', false);
    },

    setFilter(value) {
      this.activeFilter = value;
      if (this.showComposer && !this.editingItem) {
        this.form.type = value === CLIPBOARD_FILTER_ALL
          ? normalizeClipboardType(this.form.type)
          : normalizeClipboardType(value);
      }
    },

    toggleSortOrder() {
      this.sortOrder = this.sortOrder === CLIPBOARD_SORT_NEWEST
        ? CLIPBOARD_SORT_OLDEST
        : CLIPBOARD_SORT_NEWEST;
    },

    openNewItem(type = null) {
      this.cancelInlineEdit();
      this.editingItem = null;
      const filterType = this.activeFilter !== CLIPBOARD_FILTER_ALL ? this.activeFilter : null;
      const resolvedType = normalizeClipboardType(type || filterType);
      this.form = {
        ...EMPTY_FORM(),
        type: resolvedType,
      };
      this.showComposer = true;
      this.$nextTick(() => {
        this.$refs.composer?.focusContent();
      });
    },

    cancelComposer() {
      this.dismissComposer();
    },

    dismissComposer() {
      this.showComposer = false;
      this.bulkPaste = false;
      this.editingItem = null;
      this.form = EMPTY_FORM();
    },

    toggleNoteExpand(item) {
      this.$set(this.expandedNoteIds, item.id, !this.expandedNoteIds[item.id]);
    },

    startInlineEdit(item) {
      this.dismissComposer();
      this.inlineEditingId = item.id;
    },

    cancelInlineEdit() {
      this.inlineEditingId = null;
    },

    async saveInlineEdit({ item, title, content }) {
      if (!item?.id || this.saving) {
        return;
      }

      this.saving = true;
      try {
        const updated = await updateClipboardItem(item.id, {
          type: item.type,
          title,
          content,
        });
        const idx = this.items.findIndex((i) => i.id === updated.id);
        if (idx !== -1) {
          this.$set(this.items, idx, updated);
        }
        this.cancelInlineEdit();
        toastr.success('Clipboard item updated.');
      } catch (err) {
        console.error('[Clipboard] inline save failed', err);
        toastr.error('Could not update clipboard item.');
      } finally {
        this.saving = false;
      }
    },

    onExternalItemCreated(item) {
      if (!item?.id) {
        return;
      }
      const idx = this.items.findIndex((i) => i.id === item.id);
      if (idx === -1) {
        this.items.unshift(item);
      } else {
        this.$set(this.items, idx, item);
      }
    },

    buildPayload() {
      const content = this.form.content.trim();
      let title = this.form.title.trim();
      if (!title) {
        const firstLine = content.split('\n')[0].trim();
        title = firstLine.length > 80 ? `${firstLine.slice(0, 77)}…` : (firstLine || this.typeMeta(this.form.type).label);
      }
      return {
        type: normalizeClipboardType(this.form.type),
        title,
        content,
      };
    },

    async loadItems() {
      this.loading = true;
      this.loadError = null;
      try {
        this.items = await fetchClipboardItems();
      } catch (err) {
        console.error('[Clipboard] load failed', err);
        this.loadError = 'Could not load clipboard items.';
        this.items = [];
      } finally {
        this.loading = false;
      }
    },

    async saveItem() {
      if (!this.canSave || this.saving) return;

      const isBulkId = this.bulkPaste
        && (this.form.type === 'asin' || this.form.type === 'gtin')
        && !this.editingItem;

      if (isBulkId) {
        const lines = String(this.form.content || '')
          .split(/\n+/)
          .map((line) => line.trim())
          .filter(Boolean);

        if (!lines.length) {
          return;
        }

        this.saving = true;
        try {
          for (const line of lines) {
            await createClipboardItem({
              type: this.form.type,
              title: '',
              content: line,
            });
          }
          await this.loadItems();
          toastr.success(`Saved ${lines.length} item${lines.length !== 1 ? 's' : ''} to clipboard.`);
          this.dismissComposer();
        } catch (err) {
          console.error('[Clipboard] bulk save failed', err);
          toastr.error('Could not save clipboard items.');
        } finally {
          this.saving = false;
        }
        return;
      }

      const payload = this.buildPayload();
      this.saving = true;
      try {
        if (this.editingItem) {
          const updated = await updateClipboardItem(this.editingItem.id, payload);
          const idx = this.items.findIndex((i) => i.id === updated.id);
          if (idx !== -1) {
            this.$set(this.items, idx, updated);
          }
          toastr.success('Clipboard item updated.');
        } else {
          const created = await createClipboardItem(payload);
          this.items.unshift(created);
          toastr.success('Saved to clipboard.');
        }
        this.dismissComposer();
      } catch (err) {
        console.error('[Clipboard] save failed', err);
        toastr.error('Could not save clipboard item.');
      } finally {
        this.saving = false;
      }
    },

    async confirmDelete(item) {
      const label = item.title || item.content.slice(0, 40);
      if (!window.confirm(`Delete "${label}"?`)) {
        return;
      }

      this.deletingId = item.id;
      try {
        await deleteClipboardItem(item.id);
        this.items = this.items.filter((i) => i.id !== item.id);
        if (this.editingItem?.id === item.id) {
          this.dismissComposer();
        }
        if (this.inlineEditingId === item.id) {
          this.cancelInlineEdit();
        }
        toastr.success('Clipboard item deleted.');
      } catch (err) {
        console.error('[Clipboard] delete failed', err);
        toastr.error('Could not delete clipboard item.');
      } finally {
        this.deletingId = null;
      }
    },

    insertItem(item) {
      this.$emit('select', item);
      if (this.insertOnly) {
        this.close();
      }
    },

    copyItem(item) {
      const copied = copyTextToClipboard(item.content);
      if (copied) {
        toastr.success('Copied to clipboard.');
      } else {
        toastr.error('Could not copy to clipboard.');
      }
    },

    // "Save current" opens the composer pre-filled with the draft text (same form as
    // "New", just populated) so the user can add a label / tweak it before saving —
    // rather than saving silently. (POR-13563-2)
    saveCurrentDraft() {
      const content = String(this.draftText || '').trim();
      if (!content || this.saving) {
        return;
      }
      this.cancelInlineEdit();
      this.editingItem = null;
      this.form = {
        ...EMPTY_FORM(),
        type: 'prompt',
        content,
      };
      this.showComposer = true;
      this.$nextTick(() => {
        this.$refs.composer?.focusContent();
      });
    },
  },
};
</script>

<style scoped>
.clipboard-drawer {
  position: fixed;
  inset: 0;
  z-index: 1060;
}

.clipboard-drawer--contained {
  position: absolute;
  inset: 0;
  z-index: 200;
}

.clipboard-drawer--contained-reserve-nav {
  right: 3.25rem;
}

.clipboard-drawer--contained .clipboard-drawer__panel {
  width: 100%;
  max-width: 100%;
  left: 0;
  right: 0;
}

.clipboard-drawer--sidebar-overlay {
  position: fixed;
  z-index: 10050;
}

.clipboard-drawer--sidebar-overlay .clipboard-drawer__backdrop {
  border-radius: 0;
}

.clipboard-drawer--sidebar-overlay .clipboard-drawer__panel {
  inset: 0;
  width: 100%;
  height: 100%;
  max-width: none;
  border-left: none;
  box-shadow: none;
  display: flex;
  flex-direction: column;
}

.clipboard-drawer--sidebar-overlay .clipboard-drawer__scroll {
  flex: 1 1 auto;
  min-height: 0;
}

/* Layout show: opaque layer fully covering the existing sidebar column */
.clipboard-drawer--sidebar-overlay-full {
  background: #fff;
  overflow: hidden;
  box-shadow: -8px 0 30px rgba(15, 23, 42, 0.12);
}

.clipboard-drawer--sidebar-overlay-full .clipboard-drawer__panel {
  position: absolute;
  inset: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  max-width: none;
  border-left: none;
  box-shadow: none;
}

/* Layout show: full-height panel over the open sidebar tab content */
.clipboard-drawer--layout-sidebar {
  position: absolute;
  inset: 0;
  z-index: 50;
}

.clipboard-drawer--layout-sidebar .clipboard-drawer__panel {
  inset: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  max-width: none;
  border-left: none;
  box-shadow: -8px 0 30px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
}

.clipboard-drawer--layout-sidebar .clipboard-drawer__scroll {
  flex: 1 1 auto;
  min-height: 0;
}

.clipboard-drawer__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
}

.clipboard-drawer__panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 400px;
  max-width: calc(100vw - 16px);
  background: #fff;
  border-left: 1px solid #e5e7eb;
  box-shadow: -8px 0 30px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

@media (min-width: 640px) {
  .clipboard-drawer:not(.clipboard-drawer--sidebar-overlay):not(.clipboard-drawer--contained) .clipboard-drawer__panel {
    width: 400px;
  }
}

.clipboard-drawer-enter-active,
.clipboard-drawer-leave-active {
  transition: opacity 300ms ease;
}

.clipboard-drawer-enter,
.clipboard-drawer-leave-to {
  opacity: 0;
}

.clipboard-drawer-enter-active .clipboard-drawer__backdrop,
.clipboard-drawer-leave-active .clipboard-drawer__backdrop {
  transition: opacity 300ms ease;
}

.clipboard-drawer-enter .clipboard-drawer__backdrop,
.clipboard-drawer-leave-to .clipboard-drawer__backdrop {
  opacity: 0;
}

.clipboard-drawer-enter-active .clipboard-drawer__panel,
.clipboard-drawer-leave-active .clipboard-drawer__panel {
  transition: transform 500ms ease;
}

.clipboard-drawer-enter .clipboard-drawer__panel,
.clipboard-drawer-leave-to .clipboard-drawer__panel {
  transform: translateX(100%);
}

.clipboard-drawer--layout-sidebar.clipboard-drawer-enter .clipboard-drawer__panel,
.clipboard-drawer--layout-sidebar.clipboard-drawer-leave-to .clipboard-drawer__panel,
.clipboard-drawer--sidebar-overlay-full.clipboard-drawer-enter .clipboard-drawer__panel,
.clipboard-drawer--sidebar-overlay-full.clipboard-drawer-leave-to .clipboard-drawer__panel {
  transform: translateX(100%);
}

.clipboard-drawer__close {
  position: absolute;
  right: 16px;
  top: 16px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: #6b7280;
  border-radius: 4px;
  opacity: 0.7;
  transition: opacity 120ms ease, background 120ms ease;
}

.clipboard-drawer__close:hover {
  opacity: 1;
  background: #f3f4f6;
  color: #111827;
}

.clipboard-drawer__head {
  flex-shrink: 0;
  padding: 20px 20px 12px;
}

.clipboard-drawer__head-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 32px;
}

.clipboard-drawer__title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.5;
}

.clipboard-drawer__title-icon {
  flex-shrink: 0;
  color: #111827;
}

.clipboard-drawer__head-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.clipboard-drawer__save-current {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  font-size: 0.75rem;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #374151;
  transition: background 120ms ease, color 120ms ease, opacity 120ms ease;
}

.clipboard-drawer__save-current:hover:not(:disabled) {
  background: #189a73;
  color: #fff;
}

.clipboard-drawer__save-current:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.clipboard-drawer__new-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid #16a34a;
  border-radius: 6px;
  background: #fff;
  color: #16a34a;
  transition: background 120ms ease, color 120ms ease;
}

.clipboard-drawer__new-btn:hover {
  background: rgba(22, 163, 74, 0.1);
  color: #15803d;
}

.clipboard-drawer__sub {
  margin: 4px 0 0;
  font-size: 0.875rem;
  line-height: 1.35;
  color: #6b7280;
  text-align: left;
}

.clipboard-drawer__search-wrap {
  position: relative;
  margin-top: 12px;
}

.clipboard-drawer__search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.clipboard-drawer__search-input {
  display: flex;
  width: 100%;
  height: 36px;
  padding: 0 80px 0 32px;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #111827;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}

.clipboard-drawer__search-input::placeholder {
  color: #9ca3af;
}

.clipboard-drawer__search-input:focus {
  outline: none;
  border-color: #111827;
  box-shadow: 0 0 0 2px rgba(17, 24, 39, 0.08);
}

.clipboard-drawer__sort-btn {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px;
  font-size: 0.75rem;
  line-height: 1;
  color: #6b7280;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;
}

.clipboard-drawer__sort-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

.clipboard-drawer__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.clipboard-drawer__filter {
  font-size: 0.75rem;
  line-height: 1.2;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #6b7280;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;
}

.clipboard-drawer__filter:hover {
  color: #111827;
  background: #f3f4f6;
}

.clipboard-drawer__filter--active {
  background: #111827;
  border-color: #111827;
  color: #fff;
}

.clipboard-drawer__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #374151;
  transition: background 120ms ease, color 120ms ease;
}

.clipboard-drawer__btn--ghost:hover {
  background: #f3f4f6;
}

.clipboard-drawer__scroll {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
  border-top: 1px solid #e5e7eb;
}

.clipboard-drawer__scroll-inner {
  height: 100%;
  padding: 16px 20px;
  overflow-y: auto;
  background: rgba(243, 244, 246, 0.55);
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.clipboard-drawer__scroll-inner::-webkit-scrollbar {
  display: none;
}

.clipboard-drawer__empty {
  text-align: center;
  padding: 48px 12px;
  color: #6b7280;
}

.clipboard-drawer__empty-icon {
  display: block;
  margin: 0 auto 12px;
  opacity: 0.4;
  color: #9ca3af;
}

.clipboard-drawer__empty-title {
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
}

.clipboard-drawer__empty-hint {
  margin: 4px 0 12px;
  font-size: 0.75rem;
  color: #9ca3af;
}

.clipboard-drawer__empty-add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 28px;
  padding: 0 12px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #111827;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  transition: background 120ms ease, color 120ms ease;
}

.clipboard-drawer__empty-add:hover {
  background: #f3f4f6;
  color: #111827;
}

.clipboard-drawer__empty-add svg {
  flex-shrink: 0;
}

.clipboard-drawer__empty-text--error {
  color: #b91c1c;
}

.clipboard-drawer__retry {
  margin-top: 8px;
}

.clipboard-drawer__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
